import {
  EmployeesEmployeeSelectorInput,
  ShiftManagementBulkDeleteRestDaysDocument,
  ShiftManagementBulkDeleteShiftsDocument,
  ShiftManagementScheduleApplyTemplateDocument,
} from 'generated/resources/graphql'

import type { ClosePortalFn } from 'modules/core/components/Portal/useCloseProxy'
import useAsyncAction from 'modules/core/hooks/useAsyncAction'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { useRefreshStats } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { useUI } from 'modules/shift_management/hooks/useUI'
import { convertDateToBackendIfNeeded } from 'modules/shift_management/lib/dateFormatter'
import { trackAddShiftSubmitEvent } from 'modules/shift_management/lib/trackEvents'
import RestDay from 'modules/shift_management/store/internal/models/RestDay'
import Shift from 'modules/shift_management/store/internal/models/Shift'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'
import { removeDestroyedRestDaysCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedRestDaysCache'
import { removeDestroyedShiftsCache } from 'modules/shift_management/utils/apolloCache/removeDestroyedShiftsCache'
import { updateCreatedRestDaysCache } from 'modules/shift_management/utils/apolloCache/updateCreatedRestDaysCache'
import { updateCreatedShiftsCache } from 'modules/shift_management/utils/apolloCache/updateCreatedShiftsCache'

export interface Props {
  params: {
    selectedTemplateId: string
    selectedWeek?: number
    startDate: string
    endDate: string
    notes: string
    locationId: string
    workAreaId: string
    employeeSelection: EmployeesEmployeeSelectorInput
  }
  shiftsIds: string[]
  restDaysIds: string[]
  from: 'modal'
}

const useReplaceShifts = ({ params, shiftsIds, restDaysIds, from }: Props) => {
  const [{ shiftsRows }] = useShiftsRows()
  const [{ view }, dispatchSchedule] = useSchedule()
  const [, dispatchUI] = useUI()
  const { refreshStats } = useRefreshStats()

  const startDateParam = convertDateToBackendIfNeeded(params.startDate)
  const endDateParam = convertDateToBackendIfNeeded(params.endDate)
  const selectedTemplateId = params.selectedTemplateId
  // TODO: use shiftsRowsIds
  const weekPosition = params.selectedWeek || 0
  const notes = params.notes
  const locationId = params.locationId
  const workAreaId = params.workAreaId
  const employeeSelection = params.employeeSelection

  const [destroyShifts] = useMutation(ShiftManagementBulkDeleteShiftsDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementShift,
    update: (cache, { data }) => {
      removeDestroyedShiftsCache({
        cache,
        view,
        shifts: data?.shiftManagementMutations.bulkDeleteShiftmanagementShift.shifts || [],
      })
    },
  })

  const [destroyRestDays] = useMutation(ShiftManagementBulkDeleteRestDaysDocument, {
    extractorFn: (data) => data.shiftManagementMutations.bulkDeleteShiftmanagementRestDay,
    update: (cache, { data }) => {
      removeDestroyedRestDaysCache({
        cache,
        view,
        restDays: data?.shiftManagementMutations.bulkDeleteShiftmanagementRestDay.restDays || [],
      })
    },
  })

  const [applyTemplate] = useMutation(ShiftManagementScheduleApplyTemplateDocument, {
    extractorFn: (data) => data.shiftManagementMutations.applyTemplateShiftmanagementSchedule,
    update: (cache, { data }) => {
      const schedule = data?.shiftManagementMutations.applyTemplateShiftmanagementSchedule.schedule
      const shifts = schedule?.shifts || []
      const restDays = schedule?.restDays || []

      updateCreatedShiftsCache({
        cache,
        view,
        shifts,
      })
      updateCreatedRestDaysCache({
        cache,
        view,
        restDays,
      })
    },
  })

  const { running: submitting, performAction } = useAsyncAction(
    async (closePortal: ClosePortalFn) => {
      const destroyedShiftsResult = await destroyShifts({
        variables: { ids: shiftsIds },
      })

      handleResult(destroyedShiftsResult, (_errors) => {})

      const destroyedRestDaysResult = await destroyRestDays({
        variables: { ids: restDaysIds },
      })
      handleResult(destroyedRestDaysResult, (_errors) => {})

      const mutationResult = await applyTemplate({
        variables: {
          viewStartDate: view.startAt,
          viewEndDate: view.endAt,
          templateId: selectedTemplateId,
          templateWeekPosition: Number(weekPosition),
          startDate: startDateParam,
          endDate: endDateParam,
          notes,
          locationId,
          workAreaId,
          employeeSelection,
        },
      })

      const mutationHandled = handleResult(mutationResult, (_errors) => {})
      if (mutationHandled.success) {
        const { shifts: newShifts, restDays: newRestDays } = mutationHandled.data?.schedule || {}

        const shiftsFromGraphQL = (newShifts || []).map((shift) =>
          Shift.buildFromGraphQL({
            shift,
            employeeFullName: shift?.employee?.fullName || '',
            employeeAvatar: shift?.employee?.avatar?.url || '',
            employeeLocationId: shift?.employee?.locationId ?? '0',
          })
        )
        dispatchSchedule({ type: 'ADD_SHIFTS', shifts: shiftsFromGraphQL })

        const restDaysFromGraphQL = (newRestDays || []).map((restDay) =>
          RestDay.build({
            id: restDay.id,
            employee_id: restDay.employee?.id ?? '0',
            date: restDay.date,
            state: restDay.state,
            timezone: restDay.timezone || '',
            employeeFullName: restDay.employee?.fullName || '',
            employeeAvatar: restDay.employee?.avatar?.url || '',
          })
        )
        dispatchSchedule({ type: 'ADD_REST_DAYS', restDays: restDaysFromGraphQL })
        dispatchUI({ type: 'REFRESHING_STATS' })

        const shiftColors = shiftsFromGraphQL.map((shift: Shift) => shift.color)
        trackAddShiftSubmitEvent({
          view,
          employeeIds: employeeSelection.ids || [],
          shiftsCount: shiftsFromGraphQL.length,
          restDaysCount: restDaysFromGraphQL.length,
          shiftType: 'template',
          color: shiftColors,
          from,
          withSearch: '',
          advancedOptions: false,
          extras: {
            template_id: selectedTemplateId,
            starting_template_week: weekPosition + 1, // for readability so first week is 1 and not 0, and so forth
          },
        })

        const previousShiftsRows = shiftsRows.filter((row: ShiftsRow) => {
          // TODO: use shiftsRowsIds
          return employeeSelection.ids?.includes(row.id) || false
        })

        await refreshStats(previousShiftsRows)

        closePortal()
      }
    },
    {
      successMessage: i18n.t('shift_management.actions.apply_rotating_shift_success'),
    }
  )

  return {
    onReplaceShifts: performAction,
    submitting,
  }
}

export default useReplaceShifts
